import { useEffect, useState } from "react";
import useSelectedAccount from "../../../hooks/useSelectedAccount";
import { Destination } from "../../../models";
import { useGetDestinationsQuery } from "../../../redux/services/destinations";

interface UseDestinationsOptions {
  limit?: number;
  initialSearch?: string;
}

interface UseDestinationsReturn {
  // Data
  destinations: Destination[];
  totalCount: number;
  searchTotalCount: number;

  // Search
  searchInput: string;
  setSearchInput: (search: string) => void;
  searchQuery: string;
  setSearchQuery: (search: string) => void;

  // Pagination
  offset: number;
  setOffset: (offset: number) => void;
  shouldShowPagination: boolean;
  limit: number;

  // Loading states
  isInitialLoading: boolean;
  isRefetching: boolean;
  isInitialized: boolean;
  error: any;

  // Actions
  refetch: () => void;
}

export const useDestinations = ({
  limit = 20,
  initialSearch = "",
}: UseDestinationsOptions = {}): UseDestinationsReturn => {
  const { accountId } = useSelectedAccount();

  // Search state
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [searchQuery, setSearchQuery] = useState(initialSearch);

  // Pagination and totals
  const [totalCount, setTotalCount] = useState(0);
  const [searchTotalCount, setSearchTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);

  // API query
  const {
    data,
    isLoading: isInitialLoading,
    refetch,
    isFetching: isRefetching,
    isUninitialized,
    error,
  } = useGetDestinationsQuery(
    {
      account_id: accountId ?? "",
      offset,
      limit,
      search: searchQuery ?? "",
      include_total_count: true,
    },
    { skip: !accountId }
  );

  // Reset states when account changes
  useEffect(() => {
    setTotalCount(0);
    setSearchTotalCount(0);
    setOffset(0);
    setSearchInput("");
    setSearchQuery("");
  }, [accountId]);

  // Handle initialSearch changes
  useEffect(() => {
    setSearchInput(initialSearch);
    setSearchQuery(initialSearch);
  }, [initialSearch]);

  // Refetch when account or offset changes
  useEffect(() => {
    if (accountId) {
      refetch();
    }
  }, [accountId, offset, refetch]);

  // Update total count
  useEffect(() => {
    if (data?.total_count !== undefined) {
      setTotalCount(data.total_count);
    }
  }, [data?.total_count]);

  // Update search total when search is active
  useEffect(() => {
    if (searchQuery && data?.total_count !== undefined) {
      setSearchTotalCount(data.total_count);
    }
  }, [data?.total_count, searchQuery]);

  // Reset offset when search query changes
  useEffect(() => {
    setOffset(0);
  }, [searchQuery]);

  // Derived values
  const destinations = data?.data ?? [];
  const effectiveTotalCount = searchQuery ? searchTotalCount : totalCount;
  const shouldShowPagination = effectiveTotalCount > limit;

  return {
    // Data
    destinations,
    totalCount: effectiveTotalCount,
    searchTotalCount,

    // Search
    searchInput,
    setSearchInput,
    searchQuery,
    setSearchQuery,

    // Pagination
    offset,
    setOffset,
    shouldShowPagination,
    limit,

    // Loading states
    isInitialLoading,
    isRefetching,
    isInitialized: !isUninitialized,
    error,

    // Actions
    refetch,
  };
};

export default useDestinations;
